import React, { useEffect, useState, useCallback, useRef } from "react";
import { Upload, ImageOff, Trash2, Plus, Pencil, ArrowUp, ArrowDown, Copy, Save, Eye, EyeOff, FileText } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { invalidateSiteContent } from "@/hooks/useSiteContent";
import { toast } from "sonner";

// ---------------------------------------------------------------------------
// CmsList — componente RIUTILIZZABILE per qualsiasi lista di elementi ripetibili.
// Fornisce: + Aggiungi, Modifica (modale), ↑ ↓, Visibile/Nascosto, Duplica, Elimina.
// Basta passare un `fields` schema; nessuna logica dedicata per tab.
// ---------------------------------------------------------------------------
const defaultData = (fields) => {
  const d = {};
  fields.forEach((f) => {
    if (f.type === "bilingual" || f.type === "bilingual-textarea") { d[`${f.name}_it`] = ""; d[`${f.name}_en`] = ""; }
    else d[f.name] = f.default ?? "";
  });
  return d;
};

const CmsItemDialog = ({ open, onClose, onSave, fields, initial, title }) => {
  const [data, setData] = useState(initial || {});
  useEffect(() => { setData(initial || {}); }, [initial, open]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const submit = async () => { setSaving(true); try { await onSave(data); onClose(); } finally { setSaving(false); } };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="cms-item-dialog">
        <DialogHeader><DialogTitle className="font-heading">{title}</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Compila i campi e salva.</DialogDescription>
        <div className="space-y-3">
          {fields.map((f) => {
            if (f.type === "select") return (
              <div key={f.name}><Label className="text-xs">{f.label}</Label>
                <select data-testid={`cms-field-${f.name}`} value={data[f.name] || f.default || ""} onChange={(e) => set(f.name, e.target.value)}
                  className="mt-1 w-full h-10 rounded-md border border-brand-navy/20 bg-white px-3 text-sm">
                  {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            );
            if (f.type === "bilingual" || f.type === "bilingual-textarea") {
              const Comp = f.type === "bilingual-textarea" ? Textarea : Input;
              return (
                <div key={f.name} className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">{f.label} IT</Label><Comp data-testid={`cms-field-${f.name}-it`} rows={2} value={data[`${f.name}_it`] || ""} onChange={(e) => set(`${f.name}_it`, e.target.value)} className="mt-1" /></div>
                  <div><Label className="text-xs">{f.label} EN</Label><Comp data-testid={`cms-field-${f.name}-en`} rows={2} value={data[`${f.name}_en`] || ""} onChange={(e) => set(`${f.name}_en`, e.target.value)} className="mt-1" /></div>
                </div>
              );
            }
            const Comp = f.type === "textarea" ? Textarea : Input;
            return (
              <div key={f.name}><Label className="text-xs">{f.label}</Label>
                <Comp data-testid={`cms-field-${f.name}`} rows={2} placeholder={f.placeholder || ""} value={data[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} className="mt-1" />
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Annulla</Button>
          <Button data-testid="cms-item-save" onClick={submit} disabled={saving} className="rounded-full bg-brand-electric text-white">{saving ? "…" : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const CmsList = ({ group, title, addLabel = "Aggiungi", fields, summary, note, onChanged }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // {id?, data}

  const load = useCallback(() => {
    api.get(`/admin/cms/${group}`).then(({ data }) => setItems(data)).catch(() => {});
  }, [group]);
  useEffect(() => { load(); }, [load]);

  const notify = () => { load(); onChanged && onChanged(); };

  const openNew = () => { setEditing({ data: defaultData(fields) }); setOpen(true); };
  const openEdit = (it) => { setEditing({ id: it.id, data: { ...it.data } }); setOpen(true); };

  const saveItem = async (data) => {
    try {
      if (editing.id) await api.put(`/admin/cms/item/${editing.id}`, { data });
      else await api.post(`/admin/cms/${group}`, { data });
      toast.success("Salvato"); notify();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); throw e; }
  };

  const move = async (id, direction) => {
    setItems((cs) => { const idx = cs.findIndex((c) => c.id === id); const sw = direction === "up" ? idx - 1 : idx + 1; if (idx < 0 || sw < 0 || sw >= cs.length) return cs; const cp = [...cs]; [cp[idx], cp[sw]] = [cp[sw], cp[idx]]; return cp; });
    try { await api.post(`/admin/cms/item/${id}/move`, { direction }); notify(); } catch { toast.error("Spostamento non riuscito"); load(); }
  };
  const toggleVisible = async (it) => { try { await api.put(`/admin/cms/item/${it.id}`, { visible: !it.visible }); notify(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const duplicate = async (id) => { try { await api.post(`/admin/cms/item/${id}/duplicate`); toast.success("Duplicato"); notify(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const del = async (id) => { if (!window.confirm("Eliminare questo elemento?")) return; try { await api.delete(`/admin/cms/item/${id}`); toast.success("Eliminato"); notify(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div data-testid={`cms-list-${group}`} className="rounded-2xl border border-brand-navy/10 bg-white p-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div><h4 className="font-heading font-semibold text-brand-navy">{title}</h4>{note && <p className="text-xs text-brand-navy/50">{note}</p>}</div>
        <Button data-testid={`cms-add-${group}`} onClick={openNew} size="sm" className="rounded-full bg-brand-electric hover:bg-brand-electric/90 text-white gap-1.5"><Plus className="h-4 w-4" /> {addLabel}</Button>
      </div>
      <div className="mt-4 space-y-2">
        {items.map((it, i) => (
          <div key={it.id} data-testid={`cms-item-${group}-${i}`} className={`flex items-center gap-2 rounded-xl border border-brand-navy/10 bg-brand-light/50 p-2.5 ${!it.visible ? "opacity-60" : ""}`}>
            <div className="flex flex-col gap-0.5 shrink-0">
              <button data-testid={`cms-up-${group}-${i}`} disabled={i === 0} onClick={() => move(it.id, "up")} className="h-6 w-6 rounded border border-brand-navy/15 flex items-center justify-center text-brand-navy/60 disabled:opacity-30 hover:border-brand-electric"><ArrowUp className="h-3 w-3" /></button>
              <button data-testid={`cms-down-${group}-${i}`} disabled={i === items.length - 1} onClick={() => move(it.id, "down")} className="h-6 w-6 rounded border border-brand-navy/15 flex items-center justify-center text-brand-navy/60 disabled:opacity-30 hover:border-brand-electric"><ArrowDown className="h-3 w-3" /></button>
            </div>
            <div className="min-w-0 flex-1 text-sm text-brand-navy truncate">{summary ? summary(it.data) : (it.data.label_it || it.data.title_it || "—")}</div>
            <div className="flex items-center gap-1.5 shrink-0">
              <button data-testid={`cms-visible-${group}-${i}`} onClick={() => toggleVisible(it)} title={it.visible ? "Nascondi" : "Mostra"} className="h-8 w-8 rounded-lg border border-brand-navy/15 flex items-center justify-center text-brand-navy/60 hover:border-brand-electric">{it.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</button>
              <button data-testid={`cms-edit-${group}-${i}`} onClick={() => openEdit(it)} className="h-8 w-8 rounded-lg border border-brand-navy/15 flex items-center justify-center text-brand-navy/60 hover:border-brand-electric"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`cms-duplicate-${group}-${i}`} onClick={() => duplicate(it.id)} title="Duplica" className="h-8 w-8 rounded-lg border border-brand-navy/15 flex items-center justify-center text-brand-navy/60 hover:border-brand-electric"><Copy className="h-4 w-4" /></button>
              <button data-testid={`cms-delete-${group}-${i}`} onClick={() => del(it.id)} className="h-8 w-8 rounded-lg border border-brand-navy/15 flex items-center justify-center text-red-500 hover:border-red-400"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
        {!items.length && <p className="text-brand-navy/40 text-sm text-center py-4">Nessun elemento. Usa "{addLabel}" per crearne uno.</p>}
      </div>
      <CmsItemDialog open={open} onClose={() => setOpen(false)} onSave={saveItem} fields={fields}
        initial={editing?.data} title={editing?.id ? "Modifica elemento" : `Nuovo — ${title}`} />
    </div>
  );
};

// Schemi campi per i gruppi CMS
const HEADER_NAV_FIELDS = [
  { name: "label", label: "Testo", type: "bilingual" },
  { name: "url", label: "Destinazione (es. /prodotti o https://...)", type: "text", placeholder: "/prodotti" },
];
const FOOTER_ITEM_FIELDS = [
  { name: "type", label: "Tipo", type: "select", default: "link", options: [
    { value: "link", label: "Link" }, { value: "social", label: "Social" }, { value: "email", label: "Email" },
    { value: "phone", label: "Telefono" }, { value: "whatsapp", label: "WhatsApp" }, { value: "address", label: "Indirizzo" }, { value: "text", label: "Testo" },
  ] },
  { name: "label", label: "Etichetta", type: "bilingual" },
  { name: "value", label: "Valore (URL / email / numero / testo)", type: "text" },
];
const HOME_STEP_FIELDS = [
  { name: "title", label: "Titolo", type: "bilingual" },
  { name: "text", label: "Testo", type: "bilingual-textarea" },
];
const HOME_BLOCK_FIELDS = [
  { name: "kind", label: "Tipo blocco", type: "select", default: "info", options: [
    { value: "info", label: "Informativo" }, { value: "cta", label: "CTA (con pulsante)" }, { value: "promo", label: "Promo" },
  ] },
  { name: "title", label: "Titolo", type: "bilingual" },
  { name: "text", label: "Testo", type: "bilingual-textarea" },
  { name: "button_label", label: "Testo pulsante", type: "bilingual" },
  { name: "button_url", label: "Link pulsante", type: "text", placeholder: "/prodotti" },
];
const FAQ_FIELDS = [
  { name: "question", label: "Domanda", type: "bilingual" },
  { name: "answer", label: "Risposta", type: "bilingual-textarea" },
];
const SMART_SERVICE_FIELDS = [
  { name: "title", label: "Titolo", type: "bilingual" },
  { name: "desc", label: "Descrizione", type: "bilingual-textarea" },
  { name: "button_label", label: "Etichetta pulsante", type: "bilingual" },
  { name: "url", label: "Destinazione (es. /social-flyer-creator o https://...)", type: "text" },
  { name: "badge", label: "Badge", type: "select", default: "", options: [
    { value: "", label: "Nessuno" }, { value: "premium", label: "Premium" }, { value: "new", label: "NUOVO" }, { value: "soon", label: "In arrivo" },
  ] },
  { name: "key", label: "Chiave icona/immagine (cv, flyer, booking) — opzionale", type: "text" },
];

// ---------------------------------------------------------------------------
// CONTENUTI SITO (Hero, Home, Header, Footer) — campi singoli + liste dinamiche
// ---------------------------------------------------------------------------
const SECTIONS = [
  {
    key: "hero", label: "Hero (Home)", hasImage: true,
    fields: [
      { base: "badge", label: "Badge" },
      { base: "title", label: "Titolo principale" },
      { base: "titleAccent", label: "Titolo (accento colorato)" },
      { base: "subtitle", label: "Sottotitolo", multiline: true },
      { base: "cta1", label: "Pulsante 1" },
      { base: "cta2", label: "Pulsante 2" },
    ],
  },
  {
    key: "home", label: "Sezioni Home (titoli)",
    fields: [
      { base: "categoriesTitle", label: "Titolo 'Categorie'" },
      { base: "categoriesSub", label: "Sottotitolo 'Categorie'", multiline: true },
      { base: "featuredTitle", label: "Titolo 'In evidenza'" },
      { base: "featuredSub", label: "Sottotitolo 'In evidenza'" },
      { base: "howTitle", label: "Titolo 'Come funziona'" },
      { base: "smartTitle", label: "Titolo 'Servizi Smart'" },
      { base: "smartSub", label: "Sottotitolo 'Servizi Smart'", multiline: true },
    ],
  },
  {
    key: "header", label: "Header — etichetta menu Categorie",
    fields: [
      { base: "nav_categories", label: "Etichetta menu 'Categorie'" },
    ],
  },
  {
    key: "footer", label: "Footer (testi)",
    fields: [
      { base: "tagline", label: "Tagline" },
      { base: "rights", label: "Diritti riservati" },
    ],
    plain: [
      { field: "support_email", label: "Email assistenza (colonna Legale)" },
    ],
  },
  {
    key: "page_products", label: "Pagina Prodotti",
    fields: [
      { base: "title", label: "Titolo" },
      { base: "subtitle", label: "Sottotitolo", multiline: true },
      { base: "seoTitle", label: "SEO Title" },
      { base: "seoDesc", label: "SEO Description", multiline: true },
    ],
  },
  {
    key: "page_about", label: "Pagina Chi siamo",
    fields: [
      { base: "title", label: "Titolo" },
      { base: "p1", label: "Paragrafo 1", multiline: true },
      { base: "p2", label: "Paragrafo 2", multiline: true },
      { base: "p3", label: "Paragrafo 3", multiline: true },
      { base: "missionTitle", label: "Titolo missione" },
      { base: "mission", label: "Testo missione", multiline: true },
    ],
  },
  {
    key: "page_contact", label: "Pagina Contatti",
    fields: [
      { base: "title", label: "Titolo" },
      { base: "subtitle", label: "Sottotitolo", multiline: true },
      { base: "asideTitle", label: "Titolo riquadro laterale" },
      { base: "asideBody", label: "Testo riquadro laterale", multiline: true },
    ],
  },
  {
    key: "page_smart", label: "Pagina Servizi Smart",
    fields: [
      { base: "badge", label: "Badge" },
      { base: "title", label: "Titolo" },
      { base: "subtitle", label: "Sottotitolo", multiline: true },
      { base: "notify", label: "Nota in fondo", multiline: true },
    ],
  },
  {
    key: "page_faq", label: "Pagina FAQ (titolo)",
    fields: [
      { base: "title", label: "Titolo pagina" },
    ],
  },
  {
    key: "page_blog", label: "Pagina Blog",
    fields: [
      { base: "title", label: "Titolo" },
      { base: "subtitle", label: "Sottotitolo", multiline: true },
      { base: "seoTitle", label: "SEO Title" },
      { base: "seoDesc", label: "SEO Description", multiline: true },
    ],
  },
  {
    key: "home_blog", label: "Home — sezione 'Dal Blog'",
    fields: [
      { base: "title", label: "Titolo" },
      { base: "subtitle", label: "Sottotitolo", multiline: true },
    ],
    switches: [{ field: "visible", label: "Mostra la sezione in Home" }],
  },
];

const BiField = ({ label, multiline, valIt, valEn, onIt, onEn, tid }) => {
  const Comp = multiline ? Textarea : Input;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <Label className="text-xs text-brand-navy/60">{label} <span className="font-mono">IT</span></Label>
        <Comp data-testid={`sc-${tid}-it`} rows={multiline ? 2 : undefined} value={valIt || ""} onChange={(e) => onIt(e.target.value)} className="mt-1" />
      </div>
      <div>
        <Label className="text-xs text-brand-navy/60">{label} <span className="font-mono">EN</span></Label>
        <Comp data-testid={`sc-${tid}-en`} rows={multiline ? 2 : undefined} value={valEn || ""} onChange={(e) => onEn(e.target.value)} className="mt-1" />
      </div>
    </div>
  );
};

export const AdminSiteContent = () => {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);

  const load = useCallback(() => {
    api.get("/site-content").then(({ data }) => setContent(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const setVal = (section, key, value) => setContent((c) => ({ ...c, [section]: { ...c[section], [key]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/site-content", content);
      setContent(data);
      invalidateSiteContent();
      toast.success("Contenuti salvati. Le modifiche sono già online.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio non riuscito"); }
    finally { setSaving(false); }
  };

  const onHeroImage = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setUploadingHero(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/site-content/hero-image", fd);
      setContent((c) => ({ ...c, hero: { ...c.hero, image_url: data.image_url } }));
      invalidateSiteContent();
      toast.success("Immagine Hero aggiornata");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Caricamento non riuscito"); }
    finally { setUploadingHero(false); }
  };

  const clearHeroImage = () => setContent((c) => ({ ...c, hero: { ...c.hero, image_url: null } }));

  if (!content) return <div className="py-10 text-center text-brand-navy/40">Caricamento…</div>;

  return (
    <div data-testid="admin-site-content" className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap sticky top-16 z-10 bg-brand-light/80 backdrop-blur py-2">
        <div>
          <h3 className="font-heading font-bold text-lg text-brand-navy">Contenuti del sito</h3>
          <p className="text-sm text-brand-navy/60">Modifica testi e immagini + aggiungi voci menu, link footer, passi e blocchi Home. Online subito, senza deploy.</p>
        </div>
        <Button data-testid="sc-save" onClick={save} disabled={saving} className="rounded-full bg-brand-electric hover:bg-brand-electric/90 text-white gap-2">
          <Save className="h-4 w-4" /> {saving ? "Salvataggio…" : "Salva testi"}
        </Button>
      </div>

      {SECTIONS.map((sec) => (
        <div key={sec.key} data-testid={`sc-section-${sec.key}`} className="rounded-2xl border border-brand-navy/10 bg-white p-6">
          <h4 className="font-heading font-semibold text-brand-navy mb-4">{sec.label}</h4>

          {sec.hasImage && (
            <div className="mb-5 flex items-center gap-4 rounded-xl bg-brand-light p-4">
              <div className="h-20 w-20 rounded-lg overflow-hidden bg-white border shrink-0 flex items-center justify-center">
                {content.hero.image_url
                  ? <img src={`${process.env.REACT_APP_BACKEND_URL}${content.hero.image_url}`} alt="" className="w-full h-full object-contain p-1" />
                  : <img src="/logo-lsh-transparent.png" alt="" className="w-full h-full object-contain p-1 opacity-60" />}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-brand-navy">Immagine Hero</p>
                <p className="text-xs text-brand-navy/50">Se vuota si usa il logo. Consigliato PNG trasparente.</p>
                <div className="mt-2 flex items-center gap-3">
                  <label className="cursor-pointer inline-flex items-center gap-1.5 text-sm font-semibold text-brand-electric hover:text-brand-orange">
                    <Upload className="h-4 w-4" /> {uploadingHero ? "Caricamento…" : "Cambia immagine"}
                    <input data-testid="sc-hero-image" type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onHeroImage} />
                  </label>
                  {content.hero.image_url && <button data-testid="sc-hero-image-clear" onClick={clearHeroImage} className="text-xs text-red-500 underline">Usa il logo</button>}
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {sec.fields.map((f) => (
              <BiField key={f.base} label={f.label} multiline={f.multiline} tid={`${sec.key}-${f.base}`}
                valIt={content[sec.key]?.[`${f.base}_it`]} valEn={content[sec.key]?.[`${f.base}_en`]}
                onIt={(v) => setVal(sec.key, `${f.base}_it`, v)} onEn={(v) => setVal(sec.key, `${f.base}_en`, v)} />
            ))}
            {sec.plain?.map((f) => (
              <div key={f.field}>
                <Label className="text-xs text-brand-navy/60">{f.label}</Label>
                <Input data-testid={`sc-${sec.key}-${f.field}`} value={content[sec.key]?.[f.field] || ""}
                  onChange={(e) => setVal(sec.key, f.field, e.target.value)} className="mt-1" />
              </div>
            ))}
            {sec.switches?.map((f) => (
              <label key={f.field} className="flex items-center gap-3 pt-1">
                <Switch data-testid={`sc-${sec.key}-${f.field}`} checked={content[sec.key]?.[f.field] !== false}
                  onCheckedChange={(v) => setVal(sec.key, f.field, v)} />
                <span className="text-sm text-brand-navy">{f.label}</span>
              </label>
            ))}
          </div>
        </div>
      ))}

      {/* Liste dinamiche riutilizzabili */}
      <CmsList group="header_nav" title="Header — voci di menu" addLabel="Nuova voce" fields={HEADER_NAV_FIELDS}
        note="Voci del menu principale. Login/Account/Carrello e Categorie restano gestiti a parte."
        onChanged={invalidateSiteContent} summary={(d) => `${d.label_it || "—"}  →  ${d.url || ""}`} />
      <CmsList group="home_step" title="Home — 'Come funziona'" addLabel="Nuovo passo" fields={HOME_STEP_FIELDS}
        note="I passi mostrati nella sezione 'Come funziona' della Home." summary={(d) => d.title_it || "—"} />
      <CmsList group="home_block" title="Home — blocchi (info / CTA / promo)" addLabel="Nuovo blocco" fields={HOME_BLOCK_FIELDS}
        note="Blocchi extra mostrati in Home (promo, call-to-action, avvisi)." summary={(d) => `[${d.kind || "info"}] ${d.title_it || "—"}`} />
      <CmsList group="footer_item" title="Footer — link, social e contatti" addLabel="Nuovo elemento" fields={FOOTER_ITEM_FIELDS}
        note="Aggiungi link, social, email, telefono, WhatsApp, indirizzo o testo nel footer." summary={(d) => `[${d.type || "link"}] ${d.label_it || d.value || "—"}`} />
      <CmsList group="faq" title="FAQ — domande e risposte" addLabel="Nuova FAQ" fields={FAQ_FIELDS}
        note="Le domande frequenti mostrate nella pagina /faq." summary={(d) => d.question_it || "—"} />
      <CmsList group="smart_service" title="Servizi Smart — card commerciali" addLabel="Nuova card" fields={SMART_SERVICE_FIELDS}
        note="Le card della pagina /servizi-smart. La logica del servizio resta nel codice; qui gestisci solo testi, CTA, badge, link, ordine e visibilità." summary={(d) => `${d.title_it || "—"}${d.badge ? ` [${d.badge}]` : ""}`} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// CATEGORIE (SEO, visibilità, ordinamento con frecce)
// ---------------------------------------------------------------------------
const emptyCat = { slug: "", name_it: "", name_en: "" };

const CategoryEditDialog = ({ open, onClose, onSaved, editing }) => {
  const [form, setForm] = useState(editing || {});
  useEffect(() => { setForm(editing || {}); }, [editing, open]);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/categories/${editing.id}`, {
        name_it: form.name_it, name_en: form.name_en,
        desc_it: form.desc_it || "", desc_en: form.desc_en || "",
        seo_title_it: form.seo_title_it || "", seo_title_en: form.seo_title_en || "",
        seo_desc_it: form.seo_desc_it || "", seo_desc_en: form.seo_desc_en || "",
      });
      toast.success("Categoria aggiornata"); onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  if (!editing) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg" data-testid="category-edit-dialog">
        <DialogHeader><DialogTitle className="font-heading">Modifica categoria — {editing.slug}</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Modifica nome e SEO della categoria.</DialogDescription>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Nome IT</Label><Input data-testid="cat-edit-name-it" value={form.name_it || ""} onChange={(e) => setForm({ ...form, name_it: e.target.value })} className="mt-1" /></div>
            <div><Label>Nome EN</Label><Input data-testid="cat-edit-name-en" value={form.name_en || ""} onChange={(e) => setForm({ ...form, name_en: e.target.value })} className="mt-1" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Descrizione IT (sottotitolo pagina)</Label><Textarea rows={2} data-testid="cat-edit-desc-it" value={form.desc_it || ""} onChange={(e) => setForm({ ...form, desc_it: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Descrizione EN</Label><Textarea rows={2} data-testid="cat-edit-desc-en" value={form.desc_en || ""} onChange={(e) => setForm({ ...form, desc_en: e.target.value })} className="mt-1" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">SEO Title IT</Label><Input value={form.seo_title_it || ""} onChange={(e) => setForm({ ...form, seo_title_it: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">SEO Title EN</Label><Input value={form.seo_title_en || ""} onChange={(e) => setForm({ ...form, seo_title_en: e.target.value })} className="mt-1" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">SEO Description IT</Label><Textarea rows={2} value={form.seo_desc_it || ""} onChange={(e) => setForm({ ...form, seo_desc_it: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">SEO Description EN</Label><Textarea rows={2} value={form.seo_desc_en || ""} onChange={(e) => setForm({ ...form, seo_desc_en: e.target.value })} className="mt-1" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Annulla</Button>
          <Button data-testid="cat-edit-save" onClick={save} disabled={saving} className="rounded-full bg-brand-electric text-white">{saving ? "…" : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const NewCategoryDialog = ({ open, onClose, onSaved }) => {
  const [form, setForm] = useState(emptyCat);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(emptyCat); }, [open]);
  const save = async () => {
    if (!form.slug || !form.name_it || !form.name_en) { toast.error("Compila slug e nomi IT/EN"); return; }
    setSaving(true);
    try { await api.post("/admin/categories", form); toast.success("Categoria creata"); onSaved(); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="category-new-dialog">
        <DialogHeader><DialogTitle className="font-heading">Nuova categoria</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Crea una nuova categoria.</DialogDescription>
        <div className="space-y-3">
          <div><Label>Slug (identificativo URL)</Label><Input data-testid="cat-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="mt-1.5" placeholder="es. libri" /></div>
          <div><Label>Nome IT</Label><Input data-testid="cat-name-it" value={form.name_it} onChange={(e) => setForm({ ...form, name_it: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Nome EN</Label><Input data-testid="cat-name-en" value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} className="mt-1.5" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Annulla</Button>
          <Button data-testid="add-category" onClick={save} disabled={saving} className="rounded-full bg-brand-electric text-white gap-2"><Plus className="h-4 w-4" /> {saving ? "…" : "Aggiungi"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const AdminCategories = () => {
  const [cats, setCats] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/categories").then(({ data }) => setCats(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const move = async (id, direction) => {
    setCats((cs) => { // ottimistico: sposta subito nella UI
      const idx = cs.findIndex((c) => c.id === id);
      const swap = direction === "up" ? idx - 1 : idx + 1;
      if (idx < 0 || swap < 0 || swap >= cs.length) return cs;
      const copy = [...cs]; [copy[idx], copy[swap]] = [copy[swap], copy[idx]]; return copy;
    });
    try { await api.post(`/admin/categories/${id}/move`, { direction }); load(); }
    catch { toast.error("Spostamento non riuscito"); load(); }
  };

  const toggleVisible = async (c) => {
    try { await api.put(`/admin/categories/${c.id}`, { visible: !c.visible }); load(); toast.success(c.visible ? "Categoria nascosta" : "Categoria visibile"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const delCategory = async (id) => {    if (!window.confirm("Eliminare questa categoria?")) return;
    try { await api.delete(`/admin/categories/${id}`); toast.success("Categoria eliminata"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const uploadImage = async (id, file, inputEl) => {
    if (inputEl) inputEl.value = "";
    if (!file) return;
    try { const fd = new FormData(); fd.append("file", file); await api.post(`/admin/categories/${id}/image`, fd); toast.success("Foto aggiornata"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Caricamento non riuscito"); }
  };

  return (
    <div data-testid="admin-categories-cms">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div><h3 className="font-heading font-bold text-lg text-brand-navy">Categorie</h3><p className="text-sm text-brand-navy/60">Aggiungi, modifica, riordina (frecce), mostra/nascondi ed elimina.</p></div>
        <Button data-testid="add-category-open" onClick={() => setNewOpen(true)} size="sm" className="rounded-full bg-brand-electric hover:bg-brand-electric/90 text-white gap-1.5"><Plus className="h-4 w-4" /> Nuova categoria</Button>
      </div>
      <div className="space-y-3">
        {cats.map((c, i) => (
          <div key={c.id} data-testid={`admin-category-${c.slug}`} className={`flex items-center gap-3 rounded-xl border border-brand-navy/10 bg-white p-3 ${!c.visible ? "opacity-60" : ""}`}>
            <div className="flex flex-col gap-0.5 shrink-0">
              <button data-testid={`cat-up-${c.slug}`} disabled={i === 0} onClick={() => move(c.id, "up")} className="h-6 w-6 rounded border border-brand-navy/15 flex items-center justify-center text-brand-navy/60 disabled:opacity-30 hover:border-brand-electric"><ArrowUp className="h-3.5 w-3.5" /></button>
              <button data-testid={`cat-down-${c.slug}`} disabled={i === cats.length - 1} onClick={() => move(c.id, "down")} className="h-6 w-6 rounded border border-brand-navy/15 flex items-center justify-center text-brand-navy/60 disabled:opacity-30 hover:border-brand-electric"><ArrowDown className="h-3.5 w-3.5" /></button>
            </div>
            <div className="h-12 w-12 rounded-lg border border-brand-navy/10 bg-brand-light overflow-hidden flex items-center justify-center shrink-0">
              {c.image_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${c.image_url}`} alt="" className="w-full h-full object-contain" /> : <ImageOff className="h-5 w-5 text-brand-navy/30" />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-brand-navy truncate">{c.name_it} <span className="text-brand-navy/40 font-normal">/ {c.name_en}</span></p>
              <p className="text-xs text-brand-navy/50 font-mono">{c.slug}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button data-testid={`cat-visible-${c.slug}`} onClick={() => toggleVisible(c)} title={c.visible ? "Nascondi" : "Mostra"} className="h-8 w-8 rounded-lg border border-brand-navy/15 flex items-center justify-center text-brand-navy/60 hover:border-brand-electric">
                {c.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              </button>
              <label data-testid={`cat-image-upload-${c.slug}`} className="cursor-pointer h-8 w-8 rounded-lg border border-brand-navy/15 flex items-center justify-center text-brand-electric hover:border-brand-electric" title="Cambia foto">
                <Upload className="h-4 w-4" />
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => uploadImage(c.id, e.target.files?.[0], e.target)} />
              </label>
              <button data-testid={`cat-edit-${c.slug}`} onClick={() => { setEditing(c); setEditOpen(true); }} className="h-8 w-8 rounded-lg border border-brand-navy/15 flex items-center justify-center text-brand-navy/60 hover:border-brand-electric"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`delete-category-${c.slug}`} onClick={() => delCategory(c.id)} className="h-8 w-8 rounded-lg border border-brand-navy/15 flex items-center justify-center text-red-500 hover:border-red-400"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
        {!cats.length && <p className="text-brand-navy/40 text-center py-8">Nessuna categoria.</p>}
      </div>
      <NewCategoryDialog open={newOpen} onClose={() => setNewOpen(false)} onSaved={load} />
      <CategoryEditDialog open={editOpen} editing={editing} onClose={() => setEditOpen(false)} onSaved={load} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// MEDIA MANAGER (sola lettura + copia URL)
// ---------------------------------------------------------------------------
export const AdminMedia = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const load = useCallback(() => {
    api.get("/admin/media").then(({ data }) => setFiles(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  const onUpload = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post("/admin/media/upload", fd);
      toast.success("File caricato");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Caricamento non riuscito"); }
    finally { setUploading(false); }
  };
  const copy = (url) => {
    const full = `${process.env.REACT_APP_BACKEND_URL}${url}`;
    navigator.clipboard?.writeText(full).then(() => toast.success("URL copiato")).catch(() => toast.error("Copia non riuscita"));
  };
  return (
    <div data-testid="admin-media">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <h3 className="font-heading font-bold text-lg text-brand-navy">Media</h3>
          <p className="text-sm text-brand-navy/60">{files.length} file. Carica nuovi media, anteprima e copia URL pubblico.</p>
        </div>
        <label data-testid="media-upload" className="cursor-pointer inline-flex items-center gap-1.5 rounded-full bg-brand-electric hover:bg-brand-electric/90 text-white text-sm font-semibold px-4 py-2">
          <Upload className="h-4 w-4" /> {uploading ? "Caricamento…" : "Carica media"}
          <input type="file" className="hidden" onChange={onUpload} />
        </label>
      </div>
      {loading ? <div className="py-10 text-center text-brand-navy/40">Caricamento…</div> : (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {files.map((f) => (
          <div key={f.id} data-testid={`media-item-${f.id}`} className="rounded-xl border border-brand-navy/10 bg-white overflow-hidden flex flex-col">
            <div className="aspect-square bg-brand-light flex items-center justify-center overflow-hidden">
              {f.is_image ? <img src={`${process.env.REACT_APP_BACKEND_URL}${f.url}?w=300`} alt={f.filename} loading="lazy" className="w-full h-full object-contain p-2" />
                : <FileText className="h-10 w-10 text-brand-navy/30" />}
            </div>
            <div className="p-2">
              <p className="text-xs text-brand-navy/70 truncate" title={f.filename}>{f.filename}</p>
              <button data-testid={`media-copy-${f.id}`} onClick={() => copy(f.url)} className="mt-1 inline-flex items-center gap-1 text-[11px] text-brand-electric hover:text-brand-orange"><Copy className="h-3 w-3" /> Copia URL</button>
            </div>
          </div>
        ))}
        {!files.length && <p className="text-brand-navy/40 col-span-full text-center py-8">Nessun file.</p>}
      </div>
      )}
    </div>
  );
};
